import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { MdCall, MdClose, MdEmail, MdSms, MdVideocam } from 'react-icons/md';
import { BaseURL } from '../../config/apiUrl';
import { Get } from '../../Axios/AxiosFunctions';
import Loader from '../../Component/Loader';
import EditableSummary, { formatTranscript } from '../../Component/EditableSummary';
import SendSmsDialog from '../ContactsNew/components/SendSmsDialog';
import TeamChat from './TeamChat';
import classes from './Comms.module.css';

const TYPES = [
  { key: 'all', label: 'Everything' },
  { key: 'call', label: 'Calls' },
  { key: 'meeting', label: 'Zoom meetings' },
  { key: 'sms', label: 'Texts' },
  { key: 'email', label: 'Emails' },
];
const PARTIES = [
  { key: 'all', label: 'Everyone' },
  { key: 'seller', label: 'Seller' },
  { key: 'buyer', label: 'Buyer' },
];
const ICON = { call: MdCall, meeting: MdVideocam, sms: MdSms, email: MdEmail };

/**
 * Every call, Zoom meeting, text and email with this listing's seller and,
 * once there is one, its buyer. Read-only apart from editing a call summary.
 */
const ListingCommunications = ({ listingId }) => {
  const { access_token: accessToken, company_id: companyId } = useSelector(
    (state) => state.authReducer,
  );
  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState('all');
  const [party, setParty] = useState('all');
  const [withOutlook, setWithOutlook] = useState(false);
  const [transcript, setTranscript] = useState(null); // {title, text} | 'loading'
  const [textTo, setTextTo] = useState(null); // contact being texted

  const load = useCallback(async () => {
    if (!listingId) return;
    setLoading(true);
    const res = await Get(
      BaseURL(
        `communications-feed/business/${listingId}${withOutlook ? '?includeOutlook=1' : ''}`,
      ),
      accessToken,
      companyId,
    );
    setFeed(res?.data?.data || null);
    setLoading(false);
  }, [listingId, withOutlook, accessToken, companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const items = useMemo(
    () =>
      (feed?.items || []).filter(
        (i) => (type === 'all' || i.type === type) && (party === 'all' || i.party === party),
      ),
    [feed, type, party],
  );

  const openTranscript = async (item) => {
    setTranscript('loading');
    const res = await Get(
      BaseURL(`communications-feed/record/${item.recordId}/transcript`),
      accessToken,
      companyId,
    );
    const text = formatTranscript(res?.data?.data?.transcript || '');
    setTranscript({ title: `${item.title}, ${fmtDate(item.at)}`, text: text || 'No transcript was recorded for this one.' });
  };

  if (loading && !feed) return <Loader />;
  if (!feed) {
    return <div className={classes.container}><div className={classes.empty}>Communications could not be loaded.</div></div>;
  }

  const sellers = feed.contacts.filter((c) => c.party === 'seller');
  const buyers = feed.contacts.filter((c) => c.party === 'buyer');
  // The same person can exist as two contacts; name them once, and tell the
  // Text buttons apart by the end of the number.
  const names = (list) => [...new Set(list.map((c) => c.name))].join(', ');
  const last4 = (p) => String(p || '').replace(/\D/g, '').slice(-4);
  // One Text button per phone number: duplicate contacts often share one.
  const textable = feed.contacts
    .filter((c) => c.phone)
    .filter((c, i, all) => all.findIndex((x) => String(x.phone).replace(/\D/g, '').slice(-10) === String(c.phone).replace(/\D/g, '').slice(-10)) === i);
  const sameName = (c) => textable.filter((x) => x.name === c.name).length > 1;

  return (
    <div className={classes.container}>
      <p className={classes.intro}>
        Calls, Zoom meetings, texts and emails with{' '}
        {sellers.length ? <b>{names(sellers)}</b> : 'the seller'}
        {buyers.length ? <> and the buyer, <b>{names(buyers)}</b></> : null}.
        {' '}Matched by their phone numbers and email addresses. Calls and meetings arrive from Zoom within the hour.
      </p>

      {textable.length > 0 && (
        <div className={classes.chips} style={{ marginBottom: 12 }}>
          {textable.map((c) => (
            <button key={c.id} type="button" className={classes.chip} onClick={() => setTextTo(c)}>
              Text {c.name} ({c.party === 'seller' ? 'seller' : 'buyer'}{sameName(c) ? `, …${last4(c.phone)}` : ''})
            </button>
          ))}
        </div>
      )}

      <div className={classes.statRow}>
        <Stat label="Calls" value={feed.counts.call} />
        <Stat label="Zoom meetings" value={feed.counts.meeting} />
        <Stat label="Texts" value={feed.counts.sms} />
        <Stat label="Emails" value={feed.counts.email} sub={withOutlook ? 'Includes your Outlook' : 'Sent from the CRM'} />
      </div>

      <div className={classes.toolbar}>
        <div className={classes.chips}>
          {TYPES.map((t) => (
            <button key={t.key} type="button" className={`${classes.chip} ${type === t.key ? classes.chipOn : ''}`} onClick={() => setType(t.key)}>
              {t.label}
            </button>
          ))}
        </div>
        {buyers.length > 0 && (
          <div className={classes.chips}>
            {PARTIES.map((p) => (
              <button key={p.key} type="button" className={`${classes.chip} ${party === p.key ? classes.chipOn : ''}`} onClick={() => setParty(p.key)}>
                {p.label}
              </button>
            ))}
          </div>
        )}
        <span className={classes.spacer} />
        <label className={classes.toolbarNote}>
          <input type="checkbox" checked={withOutlook} onChange={(e) => setWithOutlook(e.target.checked)} />{' '}
          Include my Outlook email
        </label>
        {loading && <span className={classes.toolbarNote}>Loading…</span>}
      </div>

      <div className={classes.layout}>
      <div>
      {feed.contacts.length === 0 ? (
        <div className={classes.empty}>
          No seller is linked to this listing yet. Add the seller as the listing&apos;s owner and their calls and texts appear here.
        </div>
      ) : items.length === 0 ? (
        <div className={classes.empty}>Nothing here yet for this filter.</div>
      ) : (
        <div className={classes.feed}>
          {items.map((item) => {
            const Icon = ICON[item.type];
            const zoom = item.type === 'call' || item.type === 'meeting';
            return (
              <div key={`${item.type}:${item.id}`} className={classes.item}>
                <div className={`${classes.icon} ${classes[`icon_${item.type}`]}`}><Icon /></div>
                <div>
                  <div className={classes.itemHead}>
                    <span className={classes.itemTitle}>{item.title}</span>
                    <span className={`${classes.pill} ${item.party === 'seller' ? classes.pillSeller : classes.pillBuyer}`}>
                      {item.party === 'seller' ? 'Seller' : 'Buyer'}
                    </span>
                    {item.edited && <span className={`${classes.pill} ${classes.pillMuted}`}>Summary edited</span>}
                    <span className={classes.itemMeta}>{fmtDate(item.at)}</span>
                  </div>
                  {zoom ? (
                    item.summary ? (
                      <Collapsible>
                        <EditableSummary summary={{ _id: item.recordId, zoomSummary: item.summary }} />
                      </Collapsible>
                    ) : (
                      <div className={classes.itemMeta}>No summary yet.</div>
                    )
                  ) : (
                    <div className={classes.itemBody}>{item.summary || '(empty)'}</div>
                  )}
                  {zoom && (
                    <div className={classes.itemActions}>
                      <button type="button" className={classes.linkBtn} onClick={() => openTranscript(item)}>
                        View transcript
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {feed.total > feed.items.length && (
            <div className={classes.itemMeta}>Showing the latest {feed.items.length} of {feed.total}.</div>
          )}
        </div>
      )}
      </div>
      <TeamChat listingId={listingId} />
      </div>

      {textTo && (
        <SendSmsDialog
          open={!!textTo}
          contact={{ contact: textTo.phone, firstName: textTo.name }}
          onClose={() => {
            setTextTo(null);
            load(); // a sent text shows up in the feed straight away
          }}
        />
      )}

      <Dialog open={!!transcript} onClose={() => setTranscript(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          {transcript === 'loading' ? 'Loading transcript…' : transcript?.title}
          <IconButton aria-label="Close" onClick={() => setTranscript(null)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <MdClose />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {transcript === 'loading' ? <Loader /> : <div className={classes.transcript}>{transcript?.text}</div>}
        </DialogContent>
      </Dialog>
    </div>
  );
};

/** Long call summaries make the timeline hard to scan: show the first few
 *  lines, with Show more. */
const Collapsible = ({ children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <div className={open ? undefined : classes.clamped}>{children}</div>
      <button type="button" className={classes.linkBtn} onClick={() => setOpen((o) => !o)}>
        {open ? 'Show less' : 'Show more'}
      </button>
    </div>
  );
};

const Stat = ({ label, value, sub }) => (
  <div className={classes.stat}>
    <div className={classes.statLabel}>{label}</div>
    <div className={classes.statValue}>{value ?? 0}</div>
    {sub && <div className={classes.statSub}>{sub}</div>}
  </div>
);

export const fmtDate = (d) => (d ? moment(d).format('D MMM YYYY, h:mm a') : 'Date unknown');

export default ListingCommunications;
